import { z } from 'zod';

import { WEB_BASE } from './webBase';

/**
 * The slice of the legacy listing payload (`GET /api/v1/listings/<slug>`, the
 * endpoint `AgentResaleOfferPortal` loaded after address entry) that the portal
 * pages read. `flip_state` drives the gate the legacy OfferDetailPage kept in
 * FLIP_STATE_TO_NOTIFICATION; prices are null until a home is listed.
 */
export const listingDetailsSchema = z.object({
  // The Rails listing id — the strict-intake submission keys on it, not the slug.
  id: z.number(),
  flip_state: z.string().nullable(),
  listing_price_cents: z.number().nullable(),
  max_allowed_offer_price_cents: z.number().nullable(),
  // FHA offers wait out HUD's resale restriction after Opendoor's purchase.
  fha_eligible_date: z.string().nullable().default(null),
  // The closing days this home supports, as ISO dates; the legacy form blocked
  // every other day in its close-of-escrow picker.
  coe_available_dates: z.array(z.string()).nullable().default(null),
  listing_photos: z
    .array(z.looseObject({ url: z.string().optional(), photo_url: z.string().optional() }))
    .nullable(),
  // The Opendoor listing partner ("resale agent") shown as the offer's contact.
  resale_agent_lead: z
    .object({
      full_name: z.string().nullable().default(null),
      email: z.string().nullable().default(null),
      // The customer-facing address, preferred over the internal one.
      external_email: z.string().nullable().default(null),
      phone_number: z.string().nullable().default(null),
      display_phone_number: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
  address: z.object({
    human_readable: z.string(),
    state: z.string().nullable().default(null),
    market: z.object({ buyer_contact_email: z.string().nullable() }),
  }),
  // Relative Rails path to the listing's required-documents packet, or null
  // while no packet document exists (external-capacity listings never get one).
  listing_packet_redirect_url: z.string().nullable().default(null),
  // Market identifier (e.g. `columbia_sc`). Which agent identifiers the
  // submitting-as step asks for depends on it, as in the legacy portal.
  market: z.string().nullable().default(null),
});

export type ListingDetails = z.infer<typeof listingDetailsSchema>;

/** The legacy portal's renovation gate: no online offers while in renovation. */
export const isInRenovation = (listing: ListingDetails): boolean =>
  listing.flip_state === 'in_renovation';

/**
 * Which agent identifiers the submitting agent must give, mirroring the legacy
 * portal exactly: an MLS agent always gives an MLS ID, and only Nashville also
 * asks for the office ID; a non-MLS agent gives nothing, except in Atlanta,
 * which asks for a license number.
 */
const OFFICE_ID_MARKET = 'nashville';
const LICENSE_NUMBER_MARKET = 'atlanta';

export const showsMlsOfficeId = (listing: ListingDetails, agentType: string): boolean =>
  agentType === 'mls' && listing.market === OFFICE_ID_MARKET;

export const showsAgentLicenseNumber = (listing: ListingDetails, agentType: string): boolean =>
  agentType === 'non_mls' && listing.market === LICENSE_NUMBER_MARKET;

/** Markets without a configured buyer contact fall back to the shared inbox. */
export const contactEmailFor = (listing: ListingDetails): string =>
  listing.address.market.buyer_contact_email || 'homes@opendoor.com';

export const listingPhotoUrl = (listing: ListingDetails): string | undefined => {
  const photo = listing.listing_photos?.[0];
  return photo?.url ?? photo?.photo_url;
};

/**
 * Absolute URL of the listing's required-documents packet. The serializer
 * returns a relative Rails path; the Rails action answers it with a 302 to a
 * fresh presigned S3 GET, so the link never goes stale in an open tab.
 */
export const listingPacketUrl = (listing: ListingDetails): string | undefined =>
  listing.listing_packet_redirect_url != null
    ? `${WEB_BASE}${listing.listing_packet_redirect_url}`
    : undefined;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseIsoDay = (isoDate: string): Date => new Date(`${isoDate}T00:00:00`);

/** A home with no restriction date (or one already passed) can take FHA offers. */
export const isFhaEligible = (listing: ListingDetails, today: Date): boolean =>
  listing.fha_eligible_date == null || parseIsoDay(listing.fha_eligible_date) <= startOfDay(today);

/**
 * Whether the day can be picked as the close date. Only the days the home
 * supports (`coe_available_dates`) are allowed; without that list, anything
 * from today onward is.
 */
export const isAllowedCloseDate = (listing: ListingDetails, day: Date, today: Date): boolean => {
  const dates = listing.coe_available_dates;
  if (dates == null || dates.length === 0) {
    return startOfDay(day) >= startOfDay(today);
  }
  const dayStart = startOfDay(day);
  return dates.some((isoDate) => parseIsoDay(isoDate).getTime() === dayStart.getTime());
};

/**
 * Days from today until the earliest close this home supports — the "X" in the
 * design's "Closing date cannot be shorter than X days from today". Undefined
 * when the home publishes no availability.
 */
export const minCloseDaysFromToday = (listing: ListingDetails, today: Date): number | undefined => {
  const dates = listing.coe_available_dates;
  if (dates == null || dates.length === 0) {
    return undefined;
  }
  // ISO dates order lexicographically, so the minimum string is the earliest day.
  const earliest = dates.reduce((min, date) => (date < min ? date : min));
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(
    0,
    Math.round((parseIsoDay(earliest).getTime() - startOfDay(today).getTime()) / msPerDay),
  );
};
